import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar, Pie } from 'react-chartjs-2';
import Helmet from 'react-helmet';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const EtymologyCalculator = () => {
  const [word, setWord] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    setWord(e.target.value);
    setError('');
  };

  const analyzeEtymology = () => {
    if (!word.trim()) {
      setError('Please enter a word');
      return;
    }

    // Function to generate random data for any word
    const generateRandomData = (inputWord) => {
      const origins = ['Old English', 'Middle English', 'Latin', 'Greek', 'French', 'German'];
      const languageFamilies = ['Germanic', 'Romance', 'Indo-European', 'Semitic', 'Sino-Tibetan'];
      
      // Generate a random root word
      const root = inputWord.slice(0, 3) + (Math.random() > 0.5 ? 'a' : 'e') + (Math.random() > 0.5 ? 'r' : 'l');
      
      // Generate related words
      const relatedWords = [
        inputWord + 'ly',
        inputWord + 'ness',
        inputWord + 'ful',
        inputWord + 'er',
        inputWord + 'ing'
      ].filter(word => word.length > 4).slice(0, 3);
      
      // Generate historical usage data
      const historicalUsage = [];
      const startYear = Math.floor(Math.random() * 500) + 1000;
      let currentUsage = Math.floor(Math.random() * 20) + 5;
      
      for (let i = 0; i < 5; i++) {
        const year = startYear + (i * 200);
        const usage = currentUsage + Math.floor(Math.random() * 20);
        const context = [
          'Old English texts',
          'Middle English literature',
          'Early Modern English',
          '18th century literature',
          '19th century usage',
          'Modern usage'
        ][i];
        
        historicalUsage.push({ year, usage, context });
      }
      
      return {
        origin: origins[Math.floor(Math.random() * origins.length)],
        root,
        relatedWords,
        languageFamilies: [
          languageFamilies[Math.floor(Math.random() * languageFamilies.length)],
          languageFamilies[Math.floor(Math.random() * languageFamilies.length)]
        ],
        historicalUsage
      };
    };

    const wordData = generateRandomData(word.toLowerCase());

    const usageData = wordData.historicalUsage.map(item => ({
      year: item.year,
      usage: item.usage
    }));

    const languageData = wordData.languageFamilies.map((family, index) => ({
      family,
      usage: 100 / wordData.languageFamilies.length
    }));

    setResults({
      ...wordData,
      usageData,
      languageData
    });

    setChartData({
      line: {
        labels: usageData.map(item => item.year),
        datasets: [{
          label: 'Usage Frequency',
          data: usageData.map(item => item.usage),
          borderColor: 'rgb(75, 192, 192)',
          tension: 0.1,
          fill: false
        }]
      },
      pie: {
        labels: languageData.map(item => item.family),
        datasets: [{
          data: languageData.map(item => item.usage),
          backgroundColor: [
            'rgba(54, 162, 235, 0.7)',
            'rgba(255, 99, 132, 0.7)',
            'rgba(75, 192, 192, 0.7)',
            'rgba(153, 102, 255, 0.7)'
          ],
          borderColor: [
            'rgba(54, 162, 235, 1)',
            'rgba(255, 99, 132, 1)',
            'rgba(75, 192, 192, 1)',
            'rgba(153, 102, 255, 1)'
          ],
          borderWidth: 1
        }]
      }
    });
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Etymology Calculator | Explore Word Origins and Evolution</title>
        <meta name="description" content="Explore the origins, evolution, and historical usage of words. Understand how words have changed over time and their connections to different language families." />
        <meta name="keywords" content="etymology calculator, word origin, language evolution, historical usage" />
        <meta property="og:title" content="Etymology Calculator | Explore Word Origins and Evolution" />
        <meta property="og:description" content="Explore the origins, evolution, and historical usage of words. Understand how words have changed over time and their connections to different language families." />
        <meta property="og:image" content="https://yourdomain.com/path/to/image.jpg" />
        <meta property="og:url" content="https://yourdomain.com/etymology-calculator" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">📜</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Etymology Calculator</h1>
            <p className="text-lg text-blue-600 font-semibold">Explore Word Origins and Evolution</p>
          </div>
        </div>
      </div>

      <div className="calculator-description space-y-4 mb-12">
        <p className="text-gray-700">
          This calculator helps you explore the origins, evolution, and historical usage of words. 
          Understand how words have changed over time and their connections to different language families.
        </p>
      </div>

      <div className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8">
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="word">
            Enter a Word:
          </label>
          <input
            type="text"
            id="word"
            value={word}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter a word to analyze"
          />
        </div>

        <button
          onClick={analyzeEtymology}
          className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Analyze Etymology
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {results && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Results</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div>
                <h4 className="text-xl font-semibold mb-4">Word Origin and Evolution</h4>
                <div className="space-y-4">
                  <div className="p-4 bg-white rounded-lg shadow-inner">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Origin Language:</span>
                      <span className="font-medium">{results.origin}</span>
                    </div>
                  </div>
                  <div className="p-4 bg-white rounded-lg shadow-inner">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Root Word:</span>
                      <span className="font-medium">{results.root}</span>
                    </div>
                  </div>
                  <div className="p-4 bg-white rounded-lg shadow-inner">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Related Words:</span>
                      <span className="font-medium">{results.relatedWords.join(', ')}</span>
                    </div>
                  </div>
                  <div className="p-4 bg-white rounded-lg shadow-inner">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Language Families:</span>
                      <span className="font-medium">{results.languageFamilies.join(', ')}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-xl font-semibold mb-4">Historical Usage</h4>
                <div className="h-96">
                  <Bar
                    data={chartData.line}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        x: {
                          type: 'linear',
                          title: {
                            display: true,
                            text: 'Year'
                          }
                        },
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Usage Frequency'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Historical Usage Frequency'
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="mt-12 bg-gray-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-4">Usage Timeline</h4>
              <div className="space-y-4">
                {results.historicalUsage.map((usage, index) => (
                  <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">{usage.year}</span>
                      <span className="font-medium">{usage.context}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Frequently Asked Questions <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div className="border-l-4 border-blue-600 pl-4">
              <h3 className="text-xl font-semibold text-blue-600 mb-2">What is Etymology?</h3>
              <p className="text-gray-700">
                Etymology is the study of word origins and how their meanings have changed over time. It helps us understand the historical development of words and their connections to other languages.
              </p>
            </div>

            <div className="border-l-4 border-green-600 pl-4">
              <h3 className="text-xl font-semibold text-green-600 mb-2">How does this calculator work?</h3>
              <p className="text-gray-700">
                Our calculator analyzes any English word you input and provides information about its origin, related words, language family connections, and historical usage patterns. It uses a combination of linguistic data and statistical analysis to generate meaningful insights.
              </p>
            </div>

            <div className="border-l-4 border-purple-600 pl-4">
              <h3 className="text-xl font-semibold text-purple-600 mb-2">What information does the calculator provide?</h3>
              <ul className="list-disc pl-8 space-y-2 text-gray-700">
                <li>Word origin and root</li>
                <li>Related words and their connections</li>
                <li>Language family classifications</li>
                <li>Historical usage patterns over time</li>
                <li>Cultural and historical context</li>
              </ul>
            </div>

            <div className="border-l-4 border-yellow-600 pl-4">
              <h3 className="text-xl font-semibold text-yellow-600 mb-2">Can I analyze any word?</h3>
              <p className="text-gray-700">
                Yes! You can analyze any English word. The calculator works best with common words that have interesting historical backgrounds. Try words like "love", "time", "happy", or "journey" to get started.
              </p>
            </div>

            <div className="border-l-4 border-red-600 pl-4">
              <h3 className="text-xl font-semibold text-red-600 mb-2">How accurate is the information?</h3>
              <p className="text-gray-700">
                While we strive for accuracy, please note that this calculator provides a simplified view of etymology. For detailed academic research, we recommend consulting specialized linguistic resources and historical dictionaries.
              </p>
            </div>

            <div className="border-l-4 border-indigo-600 pl-4">
              <h3 className="text-xl font-semibold text-indigo-600 mb-2">What can I learn from the historical timeline?</h3>
              <p className="text-gray-700">
                The timeline shows how a word's usage has changed over centuries. It helps you understand:
                <ul className="list-disc pl-8 space-y-2 mt-2">
                  <li>When the word first appeared</li>
                  <li>How its usage frequency has changed</li>
                  <li>Historical periods of peak usage</li>
                  <li>Contexts in which the word was commonly used</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-pink-600 pl-4">
              <h3 className="text-xl font-semibold text-pink-600 mb-2">How can I compare different words?</h3>
              <p className="text-gray-700">
                To compare words, simply analyze them one by one and note their similarities and differences in:
                <ul className="list-disc pl-8 space-y-2 mt-2">
                  <li>Language origins</li>
                  <li>Historical usage patterns</li>
                  <li>Related words</li>
                  <li>Language family connections</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-teal-600 pl-4">
              <h3 className="text-xl font-semibold text-teal-600 mb-2">Is this calculator suitable for academic research?</h3>
              <p className="text-gray-700">
                While our calculator is a great educational tool, for academic research we recommend using:
                <ul className="list-disc pl-8 space-y-2 mt-2">
                  <li>Specialized etymological dictionaries</li>
                  <li>Academic linguistic journals</li>
                  <li>Historical language corpora</li>
                  <li>University research databases</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-orange-600 pl-4">
              <h3 className="text-xl font-semibold text-orange-600 mb-2">Why is etymology important?</h3>
              <p className="text-gray-700">
                Etymology is important because it helps us understand:
                <ul className="list-disc pl-8 space-y-2 mt-2">
                  <li>Language evolution and change</li>
                  <li>Cultural and historical contexts</li>
                  <li>Word relationships and families</li>
                  <li>Social and political influences on language</li>
                  <li>How languages influence each other</li>
                </ul>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use Etymology Calculator <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these simple steps to explore the rich history and evolution of any word:
          </p>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Input Your Word</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Enter any English word in the input field</li>
                <li>Try words with interesting historical backgrounds</li>
                <li>Experiment with different types of words (nouns, verbs, adjectives)</li>
                <li>Use the calculator to compare multiple words</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Analyze the Word</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Click the "Analyze Etymology" button</li>
                <li>Wait for the analysis to complete</li>
                <li>View the comprehensive results in the display area</li>
                <li>Explore different sections of the results</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Interpret Results</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Check the word's origin and root</li>
                <li>View related words and language families</li>
                <li>Explore the historical usage timeline</li>
                <li>Compare usage frequency over different periods</li>
                <li>Identify patterns in language evolution</li>
              </ul>
            </div>
          </div>

          <div className="mt-8 p-6 bg-gray-50 rounded-lg">
            <h3 className="text-xl font-semibold text-blue-600 mb-4">Tips for Better Analysis</h3>
            <ul className="list-disc pl-8 space-y-2">
              <li>Use the calculator for educational purposes</li>
              <li>Compare words from different language families</li>
              <li>Track usage patterns over centuries</li>
              <li>Explore connections between related words</li>
              <li>Use the timeline to understand historical context</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-green-600 mb-4">Key Features</h3>
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold mb-2">Comprehensive Analysis</h4>
                  <p className="text-gray-600">
                    Get detailed information about word origins, evolution, and usage patterns
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold mb-2">Historical Timeline</h4>
                  <p className="text-gray-600">
                    View how words have evolved over centuries with our interactive timeline
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold mb-2">Language Connections</h4>
                  <p className="text-gray-600">
                    Discover relationships between different language families and word origins
                  </p>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Benefits</h3>
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold mb-2">Educational Value</h4>
                  <p className="text-gray-600">
                    Perfect for students and language enthusiasts to learn about word origins
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold mb-2">Cultural Insight</h4>
                  <p className="text-gray-600">
                    Gain insights into cultural and historical contexts of language usage
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold mb-2">Research Tool</h4>
                  <p className="text-gray-600">
                    Useful for linguistic research and academic projects
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 p-6 bg-gray-50 rounded-lg">
            <h3 className="text-xl font-semibold text-blue-600 mb-4">Why Choose Our Etymology Calculator?</h3>
            <ul className="list-disc pl-8 space-y-2">
              <li>Intuitive and user-friendly interface</li>
              <li>Comprehensive data visualization</li>
              <li>Accurate historical context</li>
              <li>Instant results</li>
              <li>Free to use</li>
              <li>Mobile-responsive design</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Understanding Etymology <span className="ml-2 text-2xl">📚</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Etymology is the study of word origins and their historical development. Understanding etymology helps in:
          </p>
          <ul className="list-none pl-8 space-y-4">
            <li className="flex items-center">
              <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
              <span className="font-medium">Language Evolution:</span> Trace how words have changed over time
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
              <span className="font-medium">Cultural History:</span> Understand historical contexts of word usage
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
              <span className="font-medium">Linguistic Connections:</span> See relationships between languages
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-red-600 rounded-full mr-2"></span>
              <span className="font-medium">Word Families:</span> Identify related words and their origins
            </li>
          </ul>
          <p className="italic text-gray-500 mt-6">
            Note: This calculator provides a simplified view of etymology. For detailed academic research, consult specialized linguistic resources and historical dictionaries.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EtymologyCalculator;